import React, { useState, useEffect, useRef } from "react";
import { Autocomplete, TextField } from "@mui/material";
import {
  Card,
  Form,
  Button,
  Table,
  OverlayTrigger,
  Popover,
  Container,
  Spinner,
} from "react-bootstrap";
import SubmitButton from "components/Buttons/SubmitButton";
import { EditButton } from "components/Buttons/EditButton";
import { DeleteButton } from "components/Buttons/DeleteButton";
import SaveCancelButtons from "components/Buttons/SaveCancelButtons";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import { toast } from "react-toastify";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import {
  fetchCategoryDropdownForForms,
  fetchCategoryByDropDown,
} from "slices/category/thunk";
import { fetchSubCategoryByCategoryId } from "slices/sub-category/thunk";
import { fetchItemsBySubCategoryId } from "slices/item/thunk";
import { fetchUnitDropdownForForms } from "slices/unit/thunk";
import { Axios } from "api/axios";
import { useFormikContext, useField } from "formik";

export interface ItemDetail {
  id?: string;
  category: string;
  category_id?: string;
  sub_category: string;
  sub_category_id?: string;
  item_name: string;
  item_id?: string;
  item_code: string;
  unit: string;
  unit_id?: string;
  selling_price?: number;
  // Denormalized HSN and GST fields
  hsn_code_id?: string;
  hsn_code_name?: string;
  gst_id?: string;
  gst_percent?: number;
  length: string;
  width: string;
  height: string;
  required_quantity: string;
  item_file: File | string | null; // Single file: File for new uploads, string for existing file URL, null for no file
  remarks: string;
}

interface ItemDetailsSectionProps {
  items?: ItemDetail[];
  onChange?: (items: ItemDetail[]) => void;
  errors?: Record<string, string>;
}

// Component to handle File object preview with object URL
const FilePreviewCell: React.FC<{ file: File; index: number }> = ({
  file,
  index,
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    // Create object URL from File object
    const url = URL.createObjectURL(file);
    setObjectUrl(url);

    // Cleanup: revoke object URL when component unmounts
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  if (!objectUrl) {
    return <span style={{ fontSize: "12px" }}>{file.name || "File"}</span>;
  }

  return (
    <OverlayTrigger
      trigger={["hover", "focus"]}
      placement="top"
      overlay={
        <Popover id={`file-preview-${index}`} style={{ maxWidth: "300px" }}>
          <Popover.Header as="h6" style={{ fontSize: "12px" }}>
            File Preview
          </Popover.Header>
          <Popover.Body style={{ padding: "8px" }}>
            <img
              src={objectUrl}
              alt="File preview"
              style={{
                maxWidth: "100%",
                maxHeight: "200px",
                objectFit: "contain",
                display: "block",
              }}
              onError={(e) => {
                // If image fails to load, show message
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML =
                    '<span style="font-size: 11px; color: #666;">Preview not available</span>';
                }
              }}
            />
          </Popover.Body>
        </Popover>
      }
    >
      <span
        className="text-primary text-decoration-underline"
        style={{ fontSize: "12px", cursor: "pointer" }}
        onClick={() => {
          // Create download link for File object
          const url = URL.createObjectURL(file);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }}
      >
        {file.name || "File"}
      </span>
    </OverlayTrigger>
  );
};

// Internal component that uses Formik
const ItemDetailsSectionFormik: React.FC = () => {
  const formik = useFormikContext<any>();
  const [itemsField, itemsMeta, itemsHelpers] = useField<ItemDetail[]>("items");
  const items = itemsField.value || [];
  return (
    <ItemDetailsSectionInternal
      items={items}
      itemsMeta={itemsMeta}
      itemsHelpers={itemsHelpers}
      formik={formik}
    />
  );
};

// Internal component that uses props
const ItemDetailsSectionWithProps: React.FC<ItemDetailsSectionProps> = (
  props
) => {
  return (
    <ItemDetailsSectionInternal
      items={props.items || []}
      itemsMeta={undefined}
      itemsHelpers={undefined}
      formik={undefined}
      onChange={props.onChange}
      errors={props.errors}
    />
  );
};

// Internal component that contains the actual implementation
interface ItemDetailsSectionInternalProps {
  items: ItemDetail[];
  itemsMeta?: any;
  itemsHelpers?: any;
  formik?: any;
  onChange?: (items: ItemDetail[]) => void;
  errors?: Record<string, string>;
}

const ItemDetailsSectionInternal: React.FC<ItemDetailsSectionInternalProps> = ({
  items,
  itemsMeta,
  itemsHelpers,
  formik,
  onChange,
  errors,
}) => {
  const isFormikMode = formik !== undefined && itemsHelpers !== undefined;
  const dispatch = useDispatch<any>();
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [itemList, setItemList] = useState<any[]>([]);
  const [editableRowIndex, setEditableRowIndex] = useState<number | null>(null);
  const [originalItemBeforeEdit, setOriginalItemBeforeEdit] =
    useState<ItemDetail | null>(null);
  // Redux selectors for dropdown data
  const selectCategoryDropdown = createSelector(
    (state: any) => state.Category,
    (category) => category?.categoryDropdown || []
  );

  const selectSubCategoryDropdown = createSelector(
    (state: any) => state.SubCategory,
    (subCategory) => subCategory?.subCategoryDropdown || []
  );

  const selectItemDropdown = createSelector(
    (state: any) => state.ItemReducer,
    (item) => item?.itemDropdown || []
  );

  // Get dropdown data from Redux store

  // let subCategories = useSelector(selectSubCategoryDropdown);
  // let itemList = useSelector(selectItemDropdown);

  // console.log("categories data => ", categories);
  // console.log("items data => ", items);
  // Item table input state
  const [newItem, setNewItem] = useState<ItemDetail>({
    category: "",
    sub_category: "",
    item_name: "",
    item_code: "",
    unit: "",
    length: "",
    width: "",
    height: "",
    required_quantity: "",
    item_file: null,
    remarks: "",
    selling_price: undefined,
  });

  // File input ref for new item
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // File input refs for editable rows
  const editableFileInputRefs = useRef<{
    [key: number]: HTMLInputElement | null;
  }>({});

  const handleSubCategoryDropdown = async (categoryId: string) => {
    try {
      const response = await Axios.get(
        `/master/sub-category/dropdown/category/${categoryId}`
      );
      // console.log(" sub categories data => ", response.data);
      setSubCategories(response?.data?.data?.data || response.data?.data);
    } catch (error) {
      console.log(error);
    }
    // dispatch(fetchSubCategoryByCategoryId(categoryId)).unwrap().then((res: any) => {
    //   setSubCategories(res.data);
    // });
  };

  const handleCategoryDropdown = () => {
    dispatch(fetchCategoryByDropDown())
      .unwrap()
      .then((res: any) => {
        setCategories(res.data);
      });
  };

  const handleItemDropdown = async (
    categoryId: string,
    subCategoryId: string
  ) => {
    try {
      const response = await Axios.get(
        `/master/item/dropdown/category/${categoryId}/sub-category/${subCategoryId}`
      );
      // console.log(" item list data => ", response.data);
      setItemList(response?.data?.data || response.data?.data);
    } catch (error) {
      console.log(error);
      setItemList([]);
    }
  };
  // Load dropdown data using Redux thunks
  useEffect(() => {
    // Fetch categories dropdown
    handleCategoryDropdown();

    // Fetch units dropdown (not used directly, but may be needed for other purposes)
    // dispatch(fetchUnitDropdownForForms());
    // console.log("sub categories data in useEffect => ", subCategories);
    setItemList(items);
  }, [dispatch]);

  // Handle category change - fetch subcategories using Redux thunk
  const handleCategoryChange = (category: any) => {
    setNewItem((prev) => ({
      ...prev,
      category: category?.category_name || "",
      category_id: category?.category_id,
      sub_category: "",
      sub_category_id: undefined,
      item_name: "",
      item_id: undefined,
      item_code: "",
      unit: "", // Reset unit when category changes
    }));
    if (category?.category_id) {
      // dispatch(handleSubCategoryDropdown(category.category_id));
      handleSubCategoryDropdown(category.category_id);
    } else {
      setSubCategories([]);
    }
  };

  // Handle subcategory change - fetch items using Redux thunk
  const handleSubCategoryChange = (newValue: any) => {
    // console.log("new value => ", newValue);
    const subCategoryName =
      newValue?.sub_category_name || newValue?.subcategory_name || "";
    setNewItem((prev) => ({
      ...prev,
      sub_category: subCategoryName,
      sub_category_id: newValue?.sub_category_id || newValue?.subcategory_id,
      item_name: "",
      item_id: undefined,
      item_code: "",
      unit: "", // Reset unit when subcategory changes
    }));
    const subCategoryId = newValue?.sub_category_id || newValue?.subcategory_id;
    const categoryId = newItem.category_id; // Use category_id from current newItem state

    if (subCategoryId && categoryId) {
      handleItemDropdown(categoryId, subCategoryId);
    } else {
      setItemList([]);
    }
  };

  // Handle item change - auto-fill item code, unit, and HSN details
  const handleItemChange = (item: any) => {
    // console.log("item data in handleItemChange => ", item);
    setNewItem((prev) => {
      // Extract unit name from nested structure or denormalized field
      const unitName =
        item?.unit?.unit_name || item?.unit_name || item?.unit || "";
      const unitId = item?.unit?.unit_id || item?.unit_id;

      // Extract HSN details from nested structure or denormalized fields
      const hsnCodeId =
        item?.hsn_code?.hsn_code_id || item?.hsn_code_id || undefined;
      const hsnCodeName =
        item?.hsn_code?.hsn_code || item?.hsn_code_name || undefined;
      const gstId =
        item?.hsn_code?.gst_percent?.gst_id || item?.gst_id || undefined;
      let gstPercent: number | undefined = undefined;
      if (item?.hsn_code?.gst_percent?.gst_percent) {
        const parsed = parseFloat(item.hsn_code.gst_percent.gst_percent);
        gstPercent = isNaN(parsed) ? undefined : parsed;
      } else if (item?.gst_percent) {
        const parsed = parseFloat(String(item.gst_percent));
        gstPercent = isNaN(parsed) ? undefined : parsed;
      }

      // Extract category and sub_category from nested structure if available
      const categoryName = item?.category?.category_name || prev.category || "";
      const categoryId = item?.category?.category_id || prev.category_id;
      const subCategoryName =
        item?.sub_category?.sub_category_name || prev.sub_category || "";
      const subCategoryId =
        item?.sub_category?.sub_category_id || prev.sub_category_id;
      const itemName = item?.item_name || "";
      const itemCode = item?.item_code || "";
      const sellingPrice = item?.selling_price
        ? parseFloat(item.selling_price)
        : undefined;
      return {
        ...prev,
        category: categoryName,
        category_id: categoryId,
        sub_category: subCategoryName,
        sub_category_id: subCategoryId,
        item_name: itemName,
        item_id: item?.item_id,
        item_code: itemCode,
        // Auto-fill unit from nested unit object or denormalized field
        unit: unitName,
        unit_id: unitId,
        // Auto-fill HSN details from nested structure or denormalized fields
        hsn_code_id: hsnCodeId,
        hsn_code_name: hsnCodeName,
        gst_id: gstId,
        gst_percent: gstPercent,
        selling_price: sellingPrice,
      };
    });
  };

  // Handle file upload for new item (only one file allowed)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;

    if (selectedFile) {
      setNewItem((prev) => ({
        ...prev,
        item_file: selectedFile,
      }));
    }
  };

  // Remove file from new item
  const handleRemoveFile = () => {
    setNewItem((prev) => ({
      ...prev,
      item_file: null,
    }));
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Add item to table
  const handleAddItem = () => {
    if (!newItem.category || !newItem.sub_category || !newItem.item_name) {
      toast.warning("Please fill Category, Sub Category, and Item Name");
      return;
    }

    if (!newItem.required_quantity || newItem.required_quantity.trim() === "") {
      toast.warning("Quantity is required");
      return;
    }

    // Validate that quantity is a valid positive number
    const quantity = parseFloat(newItem.required_quantity);
    if (isNaN(quantity) || quantity <= 0) {
      toast.error("Quantity must be a valid number greater than 0");
      return;
    }

    // const isDuplicate = items.some(
    //   (item: ItemDetail) => item.item_id === newItem.item_id
    // );
    // if (isDuplicate) {
    //   toast.error("This item is already added");
    //   return;
    // }

    const updatedItems = [...items, { ...newItem }];
    if (isFormikMode && itemsHelpers && formik) {
      itemsHelpers.setValue(updatedItems);
      formik.setFieldTouched("items", true);
      // Use setTimeout to ensure state is updated before validation
      setTimeout(() => {
        formik.validateField("items");
      }, 0);
    } else if (onChange) {
      onChange(updatedItems);
    }

    // Reset new item
    setNewItem({
      category: "",
      sub_category: "",
      item_name: "",
      item_id: undefined,
      item_code: "",
      unit: "",
      unit_id: undefined,
      selling_price: undefined,
      hsn_code_id: undefined,
      hsn_code_name: undefined,
      gst_id: undefined,
      gst_percent: undefined,
      length: "",
      width: "",
      height: "",
      required_quantity: "",
      item_file: null,
      remarks: "",
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Clear subcategory and item dropdowns
    setSubCategories([]);
    setItemList([]);
  };

  // Update item in table
  const handleUpdateItem = (
    index: number,
    field: keyof ItemDetail,
    value: any
  ) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };

    if (isFormikMode && itemsHelpers && formik) {
      itemsHelpers.setValue(updatedItems);
      formik.setFieldTouched("items", true);
      // Use setTimeout to ensure state is updated before validation
      setTimeout(() => {
        formik.validateField("items");
      }, 0);
    } else if (onChange) {
      onChange(updatedItems);
    }
  };

  // Validate item before saving (used when clicking save button)
  const validateItemBeforeSave = (index: number): boolean => {
    const item = items[index];

    // Validate required_quantity
    if (
      !item.required_quantity ||
      String(item.required_quantity).trim() === ""
    ) {
      toast.warning("Quantity is required");
      return false;
    }

    // Validate that quantity is a valid positive number
    const quantity = parseFloat(String(item.required_quantity));
    if (isNaN(quantity) || quantity <= 0) {
      toast.error("Quantity must be a valid number greater than 0");
      return false;
    }

    return true;
  };

  // Delete item from table
  const handleDeleteItem = (index: number) => {
    // If deleting while in edit mode, restore the original item first
    let itemsToDelete = [...items];
    if (editableRowIndex === index && originalItemBeforeEdit) {
      // Restore the original item before deleting
      itemsToDelete[index] = originalItemBeforeEdit;
    }

    // Now delete the item (original, not modified)
    const updatedItems = itemsToDelete.filter(
      (_: any, i: number) => i !== index
    );

    // Exit edit mode if we were editing this item
    if (editableRowIndex === index) {
      setEditableRowIndex(null);
      setOriginalItemBeforeEdit(null);
    }

    if (isFormikMode && itemsHelpers && formik) {
      itemsHelpers.setValue(updatedItems);
      formik.setFieldTouched("items", true);
      // Use setTimeout to ensure state is updated before validation
      setTimeout(() => {
        formik.validateField("items");
      }, 0);
    } else if (onChange) {
      onChange(updatedItems);
    }
  };

  // Handle file upload for editable row
  const handleEditableFileUpload = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = e.target.files?.[0] || null;
    if (selectedFile) {
      handleUpdateItem(index, "item_file", selectedFile);
    }
  };

  // Remove file from editable row
  const handleRemoveEditableFile = (index: number) => {
    handleUpdateItem(index, "item_file", null);
    // Reset the file input
    if (editableFileInputRefs.current[index]) {
      editableFileInputRefs.current[index]!.value = "";
    }
  };

  const triggerEditableFileDialog = (index: number) => {
    if (editableFileInputRefs.current[index]) {
      editableFileInputRefs.current[index]!.click();
    }
  };

  // console.log("new item data => ", newItem);

  return (
    <Container fluid className="mx-none p-0 mt-3">
      <h6 className="card-title mb-3">Item Details</h6>
      <style>
        {`
          .sticky-column {
            position: relative;
          }
          .sticky-column::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 1px;
            background-color: #dee2e6;
            z-index: 12;
          }
          thead .sticky-column::before {
            z-index: 13;
          }
        `}
      </style>
      <Table hover responsive className="align-middle text-center">
        <thead>
          <tr>
            <th style={{ ...tableStyles.headerAlt, minWidth: "70px" }}>
              Sr No.
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "180px",
                width: "180px",
                whiteSpace: "nowrap",
              }}
            >
              Category
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "180px",
                width: "180px",
                whiteSpace: "nowrap",
              }}
            >
              Sub Category
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "180px",
                width: "180px",
                whiteSpace: "nowrap",
              }}
            >
              Item Name
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "140px",
                width: "140px",
                whiteSpace: "nowrap",
              }}
            >
              Item Code
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "100px",
                width: "100px",
                whiteSpace: "nowrap",
              }}
            >
              Unit
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "120px",
                width: "120px",
                whiteSpace: "nowrap",
              }}
            >
              Length
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "120px",
                width: "120px",
                whiteSpace: "nowrap",
              }}
            >
              Width
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "120px",
                width: "120px",
                whiteSpace: "nowrap",
              }}
            >
              Height
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "160px",
                width: "160px",
                whiteSpace: "nowrap",
              }}
            >
              Required Quantity
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "200px",
                width: "200px",
                whiteSpace: "nowrap",
              }}
            >
              File Upload
            </th>
            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "180px",
                width: "180px",
                whiteSpace: "nowrap",
              }}
            >
              Remarks
            </th>
            <th
              className="sticky-column"
              style={{
                ...tableStyles.headerAlt,
                ...tableStyles.actionHeader,
                minWidth: "100px",
                width: "100px",
                whiteSpace: "nowrap",
              }}
            >
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Input Row */}
          <tr>
            <td>Select Item</td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "180px",
                width: "180px",
              }}
            >
              <Autocomplete
                options={categories && categories?.length > 0 ? categories : []}
                getOptionLabel={(option: any) =>
                  typeof option === "string"
                    ? option
                    : option.category_name || ""
                }
                value={
                  categories.find(
                    (cat: any) => cat.category_id === newItem.category_id
                  ) || null
                }
                onChange={(event, newValue: any) => {
                  handleCategoryChange(newValue);
                }}
                ListboxProps={{
                  ...autocompleteDropdownStyles.ListboxProps,
                  style: {
                    maxHeight: "200px",
                    overflow: "auto",
                    zIndex: 100,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 100,
                    },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                      },
                    ],
                  },
                }}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt: any) =>
                      typeof opt === "string" ? opt : opt.category_name || ""
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Category"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                )}
              />
            </td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "180px",
                width: "180px",
              }}
            >
              <Autocomplete
                options={
                  subCategories && subCategories?.length > 0
                    ? subCategories
                    : []
                }
                getOptionLabel={(option: any) =>
                  typeof option === "string"
                    ? option
                    : option.sub_category_name || ""
                }
                value={
                  subCategories && subCategories?.length > 0
                    ? subCategories.find(
                        (sub: any) =>
                          sub.sub_category_id === newItem.sub_category_id
                      ) || null
                    : null
                }
                onChange={(event, newValue: any) => {
                  handleSubCategoryChange(newValue);
                }}
                disabled={!newItem.category}
                ListboxProps={{
                  ...autocompleteDropdownStyles.ListboxProps,
                  style: {
                    maxHeight: "200px",
                    overflow: "auto",
                    zIndex: 100,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 100,
                    },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                      },
                    ],
                  },
                }}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt: any) =>
                      typeof opt === "string"
                        ? opt
                        : opt.sub_category_name || ""
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      !newItem.category
                        ? "Select Category First"
                        : "Select Sub Category"
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                )}
              />
            </td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "180px",
                width: "180px",
              }}
            >
              <Autocomplete
                options={itemList && itemList?.length > 0 ? itemList : []}
                getOptionLabel={(option: any) =>
                  typeof option === "string" ? option : option.item_name || ""
                }
                value={
                  itemList && itemList?.length > 0
                    ? itemList.find(
                        (item: any) => item.item_id === newItem.item_id
                      ) || null
                    : null
                }
                onChange={(event, newValue: any) => {
                  handleItemChange(newValue);
                }}
                disabled={!newItem.sub_category}
                ListboxProps={{
                  ...autocompleteDropdownStyles.ListboxProps,
                  style: {
                    maxHeight: "200px",
                    overflow: "auto",
                    zIndex: 100,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 100,
                    },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                      },
                    ],
                  },
                }}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt: any) =>
                      typeof opt === "string" ? opt : opt.item_name || ""
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      !newItem.sub_category
                        ? "Select Category & Sub Category First"
                        : "Select Item"
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                )}
              />
            </td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "140px",
                width: "140px",
              }}
            >
              <TextField
                size="small"
                value={newItem.item_code || ""}
                disabled
                placeholder="Code"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "100px",
                width: "100px",
              }}
            >
              <TextField
                size="small"
                value={newItem.unit || ""}
                disabled
                placeholder="Unit"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "120px",
                width: "120px",
              }}
            >
              <TextField
                size="small"
                type="text"
                value={newItem.length}
                onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        return; // allow control keys
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault(); // block all non-digits
                      }
                    }}
                onChange={(e) =>
                  setNewItem((prev) => ({
                    ...prev,
                    length: e.target.value,
                  }))
                }
                placeholder="-"
                inputProps={{
                  min: 0,
                }}
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "120px",
                width: "120px",
              }}
            >
              <TextField
                size="small"
                type="text"
                value={newItem.width}
                onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        return; // allow control keys
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault(); // block all non-digits
                      }
                    }}
                onChange={(e) =>
                  setNewItem((prev) => ({
                    ...prev,
                    width: e.target.value,
                  }))
                }
                placeholder="-"
                inputProps={{
                  min: 0,
                }}
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "120px",
                width: "120px",
              }}
            >
              <TextField
                size="small"
                type="text"
                value={newItem.height}
                onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        return; // allow control keys
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault(); // block all non-digits
                      }
                    }}
                onChange={(e) =>
                  setNewItem((prev) => ({
                    ...prev,
                    height: e.target.value,
                  }))
                }
                placeholder="-"
                inputProps={{
                  min: 0,
                }}
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "160px",
                width: "160px",
              }}
            >
              <TextField
                size="small"
                type="text"
                value={newItem.required_quantity}
                onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        return; // allow control keys
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault(); // block all non-digits
                      }
                    }}
                onChange={(e) => {
                  setNewItem((prev) => ({
                    ...prev,
                    required_quantity: e.target.value,
                  }));
                }}
                placeholder="-"
                inputProps={{
                  min: 0,
                }}
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "200px",
                width: "200px",
              }}
            >
              <div className="position-relative">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="*/*"
                  style={{ display: "none" }}
                />
                <div
                  className="border rounded p-2 text-center"
                  style={{
                    backgroundColor: "#f9f9f9",
                    borderColor: "#ced4da",
                    borderStyle: "dashed",
                    cursor: "pointer",
                    minHeight: "60px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                  onClick={triggerFileDialog}
                >
                  {newItem.item_file ? (
                    <div className="w-100">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small
                          className="text-truncate"
                          style={{ fontSize: "11px", maxWidth: "80%" }}
                        >
                          {newItem.item_file instanceof File
                            ? newItem.item_file.name
                            : newItem.item_file}
                        </small>
                        <i
                          className="bi bi-x text-danger"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile();
                          }}
                        />
                      </div>
                      <small
                        className="text-muted"
                        style={{ fontSize: "10px" }}
                      >
                        Click to change file
                      </small>
                    </div>
                  ) : (
                    <>
                      <i
                        className="bi bi-upload mb-1"
                        style={{ fontSize: "18px" }}
                      ></i>
                      <small
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        Attach File
                      </small>
                    </>
                  )}
                </div>
              </div>
            </td>
            <td
              style={{
                ...tableStyles.cell,
                minWidth: "180px",
                width: "180px",
              }}
            >
              <TextField
                size="small"
                value={newItem.remarks}
                onChange={(e) =>
                  setNewItem((prev) => ({
                    ...prev,
                    remarks: e.target.value,
                  }))
                }
                placeholder="Remarks"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>
            <td
              className="sticky-column"
              style={{
                ...tableStyles.actionCell,
                minWidth: "100px",
                width: "100px",
              }}
            >
              <div style={tableStyles.actionButtonContainer}>
                <SubmitButton isSubmitting={false} onClick={handleAddItem} />
              </div>
            </td>
          </tr>

          {/* Data Rows */}
          {items.map((item: ItemDetail, index: number) => (
            <tr
              key={index}
              style={editableRowIndex === index ? tableStyles.rowEditing : {}}
            >
              <td style={tableStyles.cell}>{index + 1}</td>
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "180px",
                  width: "180px",
                }}
              >
                {item.category}
              </td>
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "180px",
                  width: "180px",
                }}
              >
                {item.sub_category}
              </td>
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "180px",
                  width: "180px",
                }}
              >
                {item.item_name}
              </td>
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "140px",
                  width: "140px",
                }}
              >
                {item.item_code}
              </td>
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "100px",
                  width: "100px",
                }}
              >
                {item.unit}
              </td>

              {/* LENGTH */}
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "120px",
                  width: "120px",
                }}
              >
                {editableRowIndex === index ? (
                  <TextField
                    size="small"
                    type="text"
                    value={item.length || ""}
                    onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        return; // allow control keys
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault(); // block all non-digits
                      }
                    }}
                    onChange={(e) => {
                      handleUpdateItem(index, "length", e.target.value);
                    }}
                    placeholder="-"
                    inputProps={{
                      min: 0,
                    }}
                    fullWidth
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                ) : (
                  <span>{item.length || "-"}</span>
                )}
              </td>

              {/* WIDTH */}
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "120px",
                  width: "120px",
                }}
              >
                {editableRowIndex === index ? (
                  <TextField
                    size="small"
                    type="text"
                    value={item.width || ""}
                    onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        return; // allow control keys
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault(); // block all non-digits
                      }
                    }}
                    onChange={(e) => {
                      handleUpdateItem(index, "width", e.target.value);
                    }}
                    placeholder="-"
                    inputProps={{
                      min: 0,
                    }}
                    fullWidth
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                ) : (
                  <span>{item.width || "-"}</span>
                )}
              </td>

              {/* HEIGHT */}
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "120px",
                  width: "120px",
                }}
              >
                {editableRowIndex === index ? (
                  <TextField
                    size="small"
                    type="text"
                    value={item.height || ""}
                    onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        return; // allow control keys
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault(); // block all non-digits
                      }
                    }}
                    onChange={(e) => {
                      handleUpdateItem(index, "height", e.target.value);
                    }}
                    placeholder="-"
                    inputProps={{
                      min: 0,
                    }}
                    fullWidth
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                ) : (
                  <span>{item.height || "-"}</span>
                )}
              </td>

              {/* REQUIRED QUANTITY */}
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "160px",
                  width: "160px",
                }}
              >
                {editableRowIndex === index ? (
                  <TextField
                    size="small"
                    type="text"
                    value={item.required_quantity || ""}
                    onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        return; // allow control keys
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault(); // block all non-digits
                      }
                    }}
                    onChange={(e) => {
                      handleUpdateItem(
                        index,
                        "required_quantity",
                        e.target.value
                      );
                    }}
                    placeholder="-"
                    inputProps={{
                      min: 0,
                    }}
                    fullWidth
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                ) : (
                  <span>{item.required_quantity || "-"}</span>
                )}
              </td>

              {/* FILE UPLOAD */}
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "200px",
                  width: "200px",
                }}
              >
                {editableRowIndex === index ? (
                  <div className="position-relative">
                    <input
                      type="file"
                      ref={(el) => {
                        if (el) {
                          editableFileInputRefs.current[index] = el;
                        }
                      }}
                      onChange={(e) => handleEditableFileUpload(index, e)}
                      accept="*/*"
                      style={{ display: "none" }}
                    />
                    <div
                      className="border rounded p-2 text-center"
                      style={{
                        backgroundColor: "#f9f9f9",
                        borderColor: "#ced4da",
                        borderStyle: "dashed",
                        cursor: "pointer",
                        minHeight: "60px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}
                      onClick={() => triggerEditableFileDialog(index)}
                    >
                      {item.item_file ? (
                        <div className="w-100">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <small
                              className="text-truncate"
                              style={{ fontSize: "11px", maxWidth: "80%" }}
                            >
                              {item.item_file instanceof File
                                ? item.item_file.name
                                : typeof item.item_file === "string"
                                ? item.item_file.split("/").pop() || "File"
                                : "File"}
                            </small>
                            <i
                              className="bi bi-x text-danger"
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveEditableFile(index);
                              }}
                            />
                          </div>
                          <small
                            className="text-muted"
                            style={{ fontSize: "10px" }}
                          >
                            Click to change file
                          </small>
                        </div>
                      ) : (
                        <>
                          <i
                            className="bi bi-upload mb-1"
                            style={{ fontSize: "18px" }}
                          ></i>
                          <small
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            Attach File
                          </small>
                        </>
                      )}
                    </div>
                  </div>
                ) : item.item_file ? (
                  <div>
                    {item.item_file instanceof File ? (
                      // File object - show with hover preview using object URL
                      <FilePreviewCell file={item.item_file} index={index} />
                    ) : typeof item.item_file === "string" ? (
                      // Existing file URL - show with hover preview
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="top"
                        overlay={
                          <Popover
                            id={`file-preview-${index}`}
                            style={{ maxWidth: "300px" }}
                          >
                            <Popover.Header
                              as="h6"
                              style={{ fontSize: "12px" }}
                            >
                              File Preview
                            </Popover.Header>
                            <Popover.Body style={{ padding: "8px" }}>
                              <img
                                src={item.item_file}
                                alt="File preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "200px",
                                  objectFit: "contain",
                                  display: "block",
                                }}
                                onError={(e) => {
                                  // If image fails to load, show message
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = "none";
                                  const parent = target.parentElement;
                                  if (parent) {
                                    parent.innerHTML =
                                      '<span style="font-size: 11px; color: #666;">Preview not available</span>';
                                  }
                                }}
                              />
                            </Popover.Body>
                          </Popover>
                        }
                      >
                        <a
                          href={item.item_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-underline"
                          style={{ fontSize: "12px", cursor: "pointer" }}
                        >
                          {item.item_file.split("/").pop() || "File"}
                        </a>
                      </OverlayTrigger>
                    ) : (
                      <span style={{ fontSize: "12px" }}>File</span>
                    )}
                  </div>
                ) : (
                  "-"
                )}
              </td>

              {/* REMARKS */}
              <td
                style={{
                  ...tableStyles.cell,
                  minWidth: "180px",
                  width: "180px",
                }}
              >
                {editableRowIndex === index ? (
                  <TextField
                    size="small"
                    value={item.remarks || ""}
                    onChange={(e) => {
                      handleUpdateItem(index, "remarks", e.target.value);
                    }}
                    placeholder="Remarks"
                    fullWidth
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                ) : (
                  <TruncatedTextCell
                    text={item?.remarks}
                    maxWidth="300px"
                    maxLines={4}
                    rowIndex={index}
                    fallbackText="—"
                  />
                )}
              </td>

              {/* ACTIONS */}
              <td
                className="sticky-column"
                style={
                  editableRowIndex === index
                    ? {
                        ...tableStyles.actionCellEditing,
                        minWidth: "100px",
                        width: "100px",
                      }
                    : {
                        ...tableStyles.actionCell,
                        minWidth: "100px",
                        width: "100px",
                      }
                }
              >
                {editableRowIndex === index ? (
                  <div style={tableStyles.actionButtonContainer}>
                    <SaveCancelButtons
                      onSave={() => {
                        // Validate before saving
                        if (validateItemBeforeSave(index)) {
                          setEditableRowIndex(null);
                          setOriginalItemBeforeEdit(null);
                        }
                      }}
                      onCancel={() => {
                        // Cancel editing - restore original item and exit edit mode
                        if (originalItemBeforeEdit) {
                          const updatedItems = [...items];
                          updatedItems[index] = originalItemBeforeEdit;

                          if (isFormikMode && itemsHelpers && formik) {
                            itemsHelpers.setValue(updatedItems);
                            formik.setFieldTouched("items", true);
                          } else if (onChange) {
                            onChange(updatedItems);
                          }
                        }
                        setEditableRowIndex(null);
                        setOriginalItemBeforeEdit(null);
                      }}
                    />
                  </div>
                ) : (
                  <div style={tableStyles.actionButtonContainer}>
                    <EditButton
                      onClick={() => {
                        // Store the original item before editing
                        setOriginalItemBeforeEdit({ ...items[index] });
                        setEditableRowIndex(index);
                      }}
                      iconClassName="ph-pencil"
                    />
                    <DeleteButton
                      onClick={() => handleDeleteItem(index)}
                      iconClassName="ph-trash"
                      isLoading={false}
                    />
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {(isFormikMode
        ? itemsMeta?.touched && itemsMeta?.error
        : errors?.items) && (
        <div className="text-danger small">
          {isFormikMode ? itemsMeta?.error : errors?.items}
        </div>
      )}
    </Container>
  );
};

// Main component that decides which mode to use
const ItemDetailsSection: React.FC<ItemDetailsSectionProps> = (props) => {
  // Check if props are provided (non-Formik mode) or if we should use Formik
  const usePropsMode =
    props.items !== undefined || props.onChange !== undefined;

  if (usePropsMode) {
    return <ItemDetailsSectionWithProps {...props} />;
  }

  // Use Formik mode - will throw if not in Formik provider, but that's expected
  return <ItemDetailsSectionFormik />;
};

export default ItemDetailsSection;
